import {
    MEDICAL_SUPPLY_TYPE_LABELS,
    MedicalSupply,
    searchMedicalSupplies,
} from '@/lib/api/medical-supplies/medical-supplies-api'
import { mask, unMask } from '@/lib/form-state/util'
import { HospitalSearchSelector } from '@/components/HospitalSearchSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useEntityAccess } from '@/lib/hooks/use-entity-access'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { SupplierSelector } from '@/components/SupplierSelector'
import { Fragment, useRef, useState } from 'react'
import { MdAdd, MdDelete } from 'react-icons/md'
import { MedicalSupplyItem } from '../types'

const currencyFormat = (
    value: string | number | null | undefined,
    allowEmpty = false
) => {
    if (value === null || value === undefined) value = '0'
    const digits = `${value}`.replace(/[^0-9]+/g, '')
    if (
        allowEmpty &&
        (!digits || digits === '0' || digits === '00' || digits === '000')
    )
        return ''
    return mask(digits, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    return parseInt(unMask(value, 'currency'))
}

/** Quem recebe a taxa do anestesista. Ver docs/billing-system.md §7. */
export type AnesthetistRecipientKind = 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER'

/**
 * Quem recebe pelo material (OPME) da linha.
 *
 * `SUPPLIER` é o padrão e o único que dispensa a escolha do destinatário — sem id, herda o
 * fornecedor do cadastro do material. Os demais exigem o id: não há de onde herdar, e
 * repasse sem credor não vira conta a pagar.
 */
export type SupplyRecipientKind =
    | 'SUPPLIER'
    | 'HOSPITAL'
    | 'PROFESSIONAL'
    | 'PARTNER'

interface MedicalSuppliesFieldsProps {
    supplies: MedicalSupplyItem[]
    onSuppliesChange: (supplies: MedicalSupplyItem[]) => void
    anesthetistFee: string
    onAnesthetistFeeChange: (value: string) => void
    /**
     * Destinatário da taxa do anestesista — obrigatório quando há taxa.
     *
     * Até 2026-07-30 a taxa era só cobrança e o anestesista era pago por fora: entrava no
     * preço, não gerava conta a pagar e inflava o lucro. Agora gera repasse, e repasse
     * precisa de credor — sem ele não há para quem transferir.
     */
    anesthetistRecipientKind: AnesthetistRecipientKind | null
    anesthetistRecipientId: number | null
    onAnesthetistRecipientChange: (
        kind: AnesthetistRecipientKind | null,
        entityId: number | null
    ) => void
    organizationId: number
    companyId?: number
    /**
     * Hospital já escolhido em qualquer ponto do atendimento. Serve de padrão ao marcar
     * um repasse como HOSPITAL — o atendimento acontece em um hospital só, e obrigar a
     * buscá-lo de novo a cada linha é o caminho para o repasse sair para a casa errada.
     */
    defaultHospitalId?: number | null
    /**
     * Segmento saúde. Fora dele os campos do anestesista somem — não há equivalente
     * num salão ou numa oficina. Default `true` para não mudar o comportamento de
     * quem já usava o componente.
     */
    isHealth?: boolean
}

export default function MedicalSuppliesFields({
    supplies,
    onSuppliesChange,
    anesthetistFee,
    onAnesthetistFeeChange,
    anesthetistRecipientKind,
    anesthetistRecipientId,
    onAnesthetistRecipientChange,
    organizationId,
    companyId,
    defaultHospitalId = null,
    isHealth = true,
}: MedicalSuppliesFieldsProps) {
    const { canSeeProfessionals } = useEntityAccess()
    const [msSearch, setMsSearch] = useState('')
    const [msResults, setMsResults] = useState<MedicalSupply[]>([])
    const [showMsDropdown, setShowMsDropdown] = useState(false)
    const msDropdownRef = useRef<HTMLDivElement>(null)
    const searchTimeout = useRef<ReturnType<typeof setTimeout>>(null!)

    const handleSearch = (query: string) => {
        setMsSearch(query)
        if (searchTimeout.current) clearTimeout(searchTimeout.current)
        if (!query.trim()) {
            setMsResults([])
            setShowMsDropdown(false)
            return
        }
        searchTimeout.current = setTimeout(async () => {
            const res = await searchMedicalSupplies(organizationId, query)
            if (res.success) {
                setMsResults(res.result || [])
                setShowMsDropdown(true)
            }
        }, 300)
    }

    /**
     * Altera uma linha de material. Existe porque a escolha do destinatário agora tem
     * quatro caminhos, e repetir o `supplies.map((item, i) => i === idx ? {...} : item)`
     * em cada um deixava o JSX ilegível.
     */
    const updateSupply = (idx: number, patch: Partial<MedicalSupplyItem>) =>
        onSuppliesChange(
            supplies.map((item, i) => (i === idx ? { ...item, ...patch } : item))
        )

    const suppliesTotal = supplies.reduce(
        (sum, ms) => sum + ms.quantity * ms.unitPrice,
        0
    )
    const anesthetistCents = anesthetistFee
        ? parseCurrencyToCents(anesthetistFee)
        : 0

    return (
        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
            <div className="card-body">
                <h2 className="card-title text-lg mb-4">
                    {isHealth ? 'Materiais e Anestesista' : 'Materiais'}
                </h2>

                {/* Anestesista e seu recebedor só existem na saúde. Fora dela o
                    campo fica vazio e nenhum repasse de anestesista é gerado. */}
                {isHealth && (
                <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">
                                Honorários do Anestesista
                            </span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered input-sm w-full"
                            placeholder="R$ 0,00"
                            value={anesthetistFee}
                            onChange={e =>
                                onAnesthetistFeeChange(
                                    currencyFormat(e.target.value, true)
                                )
                            }
                        />
                    </div>

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">
                                Quem recebe
                                {anesthetistCents > 0 ? ' *' : ''}
                            </span>
                        </label>
                        <select
                            className="select select-bordered select-sm w-full"
                            value={anesthetistRecipientKind ?? ''}
                            onChange={e => {
                                const kind = (e.target.value ||
                                    null) as AnesthetistRecipientKind | null
                                onAnesthetistRecipientChange(
                                    kind,
                                    // Trocar o tipo invalida o destinatário anterior —
                                    // exceto o hospital, que já é conhecido: é o mesmo
                                    // do atendimento.
                                    kind === 'HOSPITAL'
                                        ? defaultHospitalId
                                        : null
                                )
                            }}
                        >
                            <option value="">Selecione...</option>
                            <option value="HOSPITAL">Hospital</option>
                            {canSeeProfessionals && (
                                <option value="PROFESSIONAL">Profissional</option>
                            )}
                            <option value="PARTNER">Parceiro</option>
                        </select>
                    </div>

                    <div className="form-control w-full">
                        {anesthetistRecipientKind === 'HOSPITAL' && (
                            <HospitalSearchSelector
                                value={anesthetistRecipientId}
                                label="Hospital"
                                size="sm"
                                onChange={hospitalId =>
                                    onAnesthetistRecipientChange(
                                        'HOSPITAL',
                                        hospitalId
                                    )
                                }
                            />
                        )}
                        {anesthetistRecipientKind === 'PROFESSIONAL' && (
                            <ProfessionalSearchSelector
                                value={anesthetistRecipientId}
                                label="Profissional"
                                size="sm"
                                onChange={professionalId =>
                                    onAnesthetistRecipientChange(
                                        'PROFESSIONAL',
                                        professionalId
                                    )
                                }
                            />
                        )}
                        {anesthetistRecipientKind === 'PARTNER' && (
                            <PartnerSelector
                                value={anesthetistRecipientId}
                                label="Parceiro"
                                size="sm"
                                companyId={companyId}
                                onChange={partnerId =>
                                    onAnesthetistRecipientChange(
                                        'PARTNER',
                                        partnerId
                                    )
                                }
                            />
                        )}
                    </div>
                </div>

                {anesthetistCents > 0 &&
                    (!anesthetistRecipientKind || !anesthetistRecipientId) && (
                        <p className="text-sm text-warning mt-1">
                            Informe quem recebe a taxa do anestesista — é ela
                            que gera o repasse ao ser dada a baixa.
                        </p>
                    )}
                </>
                )}

                <div className="form-control w-full mt-4">
                    <label className="label">
                        <span className="label-text font-medium">Materiais</span>
                    </label>
                    <div className="relative" ref={msDropdownRef}>
                        <input
                            type="text"
                            className="input input-bordered input-sm w-full"
                            placeholder="Buscar material..."
                            value={msSearch}
                            onChange={e => handleSearch(e.target.value)}
                            onFocus={() =>
                                msResults.length > 0 && setShowMsDropdown(true)
                            }
                        />
                        {showMsDropdown && msResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                {msResults.map(ms => (
                                    <button
                                        key={ms.id}
                                        type="button"
                                        className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300/50 last:border-b-0"
                                        onClick={() => {
                                            if (
                                                supplies.some(
                                                    s =>
                                                        s.medicalSupplyId ===
                                                        ms.id
                                                )
                                            )
                                                return
                                            onSuppliesChange([
                                                ...supplies,
                                                {
                                                    medicalSupplyId: ms.id,
                                                    name: ms.name,
                                                    type: ms.type,
                                                    quantity: 1,
                                                    // Preço de VENDA: este valor entra
                                                    // no total cobrado do paciente.
                                                    // Usar o custo aqui fazia o material
                                                    // ser repassado sem margem. Cai no
                                                    // custo só quando não há preço de
                                                    // venda cadastrado, seguindo o mesmo
                                                    // `sellingPrice || price` do catálogo.
                                                    unitPrice:
                                                        ms.sellingPrice ||
                                                        ms.costPrice ||
                                                        0,
                                                    defaultSupplierId:
                                                        (ms as any)
                                                            .supplierId ?? null,
                                                },
                                            ])
                                            setMsSearch('')
                                            setShowMsDropdown(false)
                                        }}
                                    >
                                        <span className="font-medium">
                                            {ms.name}
                                        </span>
                                        <span className="text-xs text-base-content/60 ml-2">
                                            {MEDICAL_SUPPLY_TYPE_LABELS[
                                                ms.type as keyof typeof MEDICAL_SUPPLY_TYPE_LABELS
                                            ] || ms.type}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {supplies.length > 0 && (
                    <div className="overflow-x-auto mt-4">
                        <table className="table table-sm">
                            <thead>
                                {/* O nome do material NÃO é coluna: vai numa linha
                                    própria acima de cada item, como em exames e
                                    consultas. Como coluna era o campo mais largo e
                                    empurrava a tabela para a rolagem horizontal. */}
                                <tr>
                                    <th className="w-20">Qtd.</th>
                                    {/* Os dois valores da linha, com nomes que dizem de
                                        que lado estão: o primeiro é o que o PACIENTE
                                        paga, o segundo é o que a clínica PAGA a quem
                                        forneceu — é ele que vira conta a pagar. */}
                                    <th className="w-36">Valor de venda</th>
                                    <th className="w-36">Custo (repasse)</th>
                                    <th className="w-64">Quem recebe</th>
                                    <th className="text-right">Subtotal</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {supplies.map((ms, idx) => (
                                    <Fragment key={ms.medicalSupplyId}>
                                        <tr>
                                            <td
                                                colSpan={6}
                                                className="border-b-0 pb-0 align-bottom"
                                            >
                                                <span className="font-medium">
                                                    {ms.name}
                                                </span>
                                                <span className="text-xs text-base-content/60 ml-1">
                                                    (
                                                    {MEDICAL_SUPPLY_TYPE_LABELS[
                                                        ms.type as keyof typeof MEDICAL_SUPPLY_TYPE_LABELS
                                                    ] || ms.type}
                                                    )
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="input input-bordered input-sm w-16"
                                                    value={ms.quantity}
                                                    onChange={e => {
                                                        const qty =
                                                            parseInt(
                                                                e.target.value
                                                            ) || 1
                                                        onSuppliesChange(
                                                            supplies.map(
                                                                (item, i) =>
                                                                    i === idx
                                                                        ? {
                                                                              ...item,
                                                                              quantity:
                                                                                  qty,
                                                                          }
                                                                        : item
                                                            )
                                                        )
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="input input-bordered input-sm w-28"
                                                    value={
                                                        currencyFormat(
                                                            ms.unitPrice.toString(),
                                                            true
                                                        ) || currencyFormat('0')
                                                    }
                                                    onChange={e => {
                                                        const cents =
                                                            parseCurrencyToCents(
                                                                e.target.value
                                                            )
                                                        onSuppliesChange(
                                                            supplies.map(
                                                                (item, i) =>
                                                                    i === idx
                                                                        ? {
                                                                              ...item,
                                                                              unitPrice:
                                                                                  cents,
                                                                          }
                                                                        : item
                                                            )
                                                        )
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                {/* Custo: é ELE que vira repasse. Vinha vazio
                                                do cadastro na maioria dos materiais, e o
                                                repasse saía zero. */}
                                                <input
                                                    type="text"
                                                    className={`input input-bordered input-sm w-28 ${!ms.costPriceSnapshot ? 'input-warning' : ''}`}
                                                    placeholder="R$ 0,00"
                                                    value={currencyFormat(
                                                        (
                                                            ms.costPriceSnapshot ??
                                                            0
                                                        ).toString(),
                                                        true
                                                    )}
                                                    onChange={e => {
                                                        const cents =
                                                            parseCurrencyToCents(
                                                                e.target.value
                                                            )
                                                        onSuppliesChange(
                                                            supplies.map(
                                                                (item, i) =>
                                                                    i === idx
                                                                        ? {
                                                                              ...item,
                                                                              costPriceSnapshot:
                                                                                  cents,
                                                                          }
                                                                        : item
                                                            )
                                                        )
                                                    }}
                                                />
                                                <label className="label cursor-pointer justify-start gap-1 py-0">
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox checkbox-sm"
                                                        checked={
                                                            !!ms.costPriceSnapshot &&
                                                            ms.costPriceSnapshot ===
                                                                ms.unitPrice
                                                        }
                                                        onChange={e =>
                                                            onSuppliesChange(
                                                                supplies.map(
                                                                    (
                                                                        item,
                                                                        i
                                                                    ) =>
                                                                        i ===
                                                                        idx
                                                                            ? {
                                                                                  ...item,
                                                                                  // Repasse integral: o
                                                                                  // OPME é passado a custo,
                                                                                  // sem margem.
                                                                                  costPriceSnapshot:
                                                                                      e
                                                                                          .target
                                                                                          .checked
                                                                                          ? item.unitPrice
                                                                                          : undefined,
                                                                              }
                                                                            : item
                                                                )
                                                            )
                                                        }
                                                    />
                                                    <span className="label-text text-sm">
                                                        Sem margem
                                                    </span>
                                                </label>
                                            </td>
                                            <td>
                                                {/* O fornecedor do OPME muda a cada orçamento, e
                                                quem cobra pode ser o hospital de onde o material
                                                saiu, o parceiro que o trouxe ou o profissional
                                                que o adquiriu: por isso a escolha é aqui, sempre
                                                editável. */}
                                                <select
                                                    className="select select-bordered select-sm w-full mb-1"
                                                    value={
                                                        ms.recipientKind ??
                                                        'SUPPLIER'
                                                    }
                                                    onChange={e => {
                                                        const kind = e.target
                                                            .value as SupplyRecipientKind
                                                        updateSupply(idx, {
                                                            recipientKind: kind,
                                                            // Hospital do atendimento
                                                            // entra preenchido; os
                                                            // demais recomeçam vazios.
                                                            recipientId:
                                                                kind ===
                                                                'HOSPITAL'
                                                                    ? defaultHospitalId
                                                                    : null,
                                                            recipientName: null,
                                                        })
                                                    }}
                                                >
                                                    <option value="SUPPLIER">
                                                        Fornecedor
                                                    </option>
                                                    <option value="HOSPITAL">
                                                        Hospital
                                                    </option>
                                                    {canSeeProfessionals && (
                                                        <option value="PROFESSIONAL">
                                                            Profissional
                                                        </option>
                                                    )}
                                                    <option value="PARTNER">
                                                        Parceiro
                                                    </option>
                                                </select>
                                                {(ms.recipientKind ??
                                                    'SUPPLIER') ===
                                                    'SUPPLIER' && (
                                                    <SupplierSelector
                                                        value={
                                                            ms.recipientId ??
                                                            null
                                                        }
                                                        label=""
                                                        size="sm"
                                                        placeholder="Padrão do cadastro"
                                                        onChange={(
                                                            supplierId,
                                                            supplier
                                                        ) =>
                                                            updateSupply(idx, {
                                                                recipientKind:
                                                                    'SUPPLIER',
                                                                recipientId:
                                                                    supplierId,
                                                                recipientName:
                                                                    supplier?.name ??
                                                                    null,
                                                            })
                                                        }
                                                    />
                                                )}
                                                {ms.recipientKind ===
                                                    'HOSPITAL' && (
                                                    <HospitalSearchSelector
                                                        value={
                                                            ms.recipientId ??
                                                            null
                                                        }
                                                        label=""
                                                        size="sm"
                                                        onChange={(
                                                            hospitalId,
                                                            hospital
                                                        ) =>
                                                            updateSupply(idx, {
                                                                recipientKind:
                                                                    'HOSPITAL',
                                                                recipientId:
                                                                    hospitalId,
                                                                recipientName:
                                                                    hospital?.name ??
                                                                    null,
                                                            })
                                                        }
                                                    />
                                                )}
                                                {ms.recipientKind ===
                                                    'PROFESSIONAL' && (
                                                    <ProfessionalSearchSelector
                                                        value={
                                                            ms.recipientId ??
                                                            null
                                                        }
                                                        label=""
                                                        size="sm"
                                                        onChange={(
                                                            professionalId,
                                                            professional
                                                        ) =>
                                                            updateSupply(idx, {
                                                                recipientKind:
                                                                    'PROFESSIONAL',
                                                                recipientId:
                                                                    professionalId,
                                                                recipientName:
                                                                    professional?.name ??
                                                                    null,
                                                            })
                                                        }
                                                    />
                                                )}
                                                {ms.recipientKind ===
                                                    'PARTNER' && (
                                                    <PartnerSelector
                                                        value={
                                                            ms.recipientId ??
                                                            null
                                                        }
                                                        label=""
                                                        size="sm"
                                                        companyId={companyId}
                                                        onChange={(
                                                            partnerId,
                                                            partner
                                                        ) =>
                                                            updateSupply(idx, {
                                                                recipientKind:
                                                                    'PARTNER',
                                                                recipientId:
                                                                    partnerId,
                                                                recipientName:
                                                                    partner?.name ??
                                                                    null,
                                                            })
                                                        }
                                                    />
                                                )}
                                                {/* Só o fornecedor tem padrão de onde herdar.
                                                    Nos demais, linha sem destinatário é custo
                                                    cobrado do paciente que nunca vira conta a
                                                    pagar — o backend recusa, e o aviso aqui
                                                    evita a ida e volta. */}
                                                {(ms.recipientKind ??
                                                    'SUPPLIER') !== 'SUPPLIER' &&
                                                    !ms.recipientId && (
                                                        <span className="text-xs text-warning">
                                                            Escolha quem recebe.
                                                        </span>
                                                    )}
                                            </td>
                                            <td className="text-right">
                                                {new Intl.NumberFormat(
                                                    'pt-BR',
                                                    {
                                                        style: 'currency',
                                                        currency: 'BRL',
                                                    }
                                                ).format(
                                                    (ms.quantity *
                                                        ms.unitPrice) /
                                                        100
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-sm text-error"
                                                    onClick={() =>
                                                        onSuppliesChange(
                                                            supplies.filter(
                                                                (_, i) =>
                                                                    i !== idx
                                                            )
                                                        )
                                                    }
                                                >
                                                    <MdDelete />
                                                </button>
                                            </td>
                                        </tr>
                                    </Fragment>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="text-right font-semibold"
                                    >
                                        Total Materiais:
                                    </td>
                                    <td className="text-right font-semibold">
                                        {new Intl.NumberFormat('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                        }).format(suppliesTotal / 100)}
                                    </td>
                                    <td></td>
                                </tr>
                                {anesthetistCents > 0 && (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="text-right font-semibold"
                                        >
                                            Anestesista:
                                        </td>
                                        <td className="text-right font-semibold">
                                            {new Intl.NumberFormat('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL',
                                            }).format(anesthetistCents / 100)}
                                        </td>
                                        <td></td>
                                    </tr>
                                )}
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}
